import React from 'react'
import { useComplianceBarHeight } from '@hooks'
import { useComponentStatesStore, useFreespinsStore, useUltraPlayStore } from '@stores'
import {
    CollectClickedMessage,
    ExtraWinMessages,
    ExtraWinToggledMessage,
    GameUIMessageType,
    ChangeOpacityComponents,
    ChangeOpacityMessagePrefix,
    GameUIMessageScope,
    BetSliderSetVisibilityMessage,
} from 'game-ui-types'
import UltraPlayControls from './UltraPlayControls'
import UltraPlayFeatureDialog from './UltraPlayFeatureDialog'
import UltraPlayInsufficientBalanceModal from './UltraPlayInsufficientBalanceModal'
import { UltraPlaySoundEffects } from './useUltraPlaySoundEffects'
import { closeUltraPlay } from './ultraPlayClose'
import { ultraPlayAnalytics, UltraPlayCloseReason } from './ultraPlayAnalytics'

const HIDDEN_COMPONENTS = [
    ChangeOpacityComponents.JACKPOT_SECTION,
    ChangeOpacityComponents.PROMO_WIDGET,
    ChangeOpacityComponents.INFO_LINE,
    ChangeOpacityComponents.REMAINING_SPINS_BANNER,
    ChangeOpacityComponents.NAVIGATION,
    ChangeOpacityComponents.PLAYER_INFO,
    ChangeOpacityComponents.BUY_BONUS_BUTTON,
]

const setComponentsVisibility = (visible: boolean) => {
    const payload = visible ? { opacity: 1, isInteractive: true } : { opacity: 0, isInteractive: false }

    HIDDEN_COMPONENTS.forEach((component) => {
        if (
            component === ChangeOpacityComponents.BUY_BONUS_BUTTON &&
            visible &&
            useUltraPlayStore.getState().currentFeatureName === 'freespins'
        ) {
            return
        }

        window.postMessage({ type: `${ChangeOpacityMessagePrefix}${component}`, payload }, '*')

        const setVisibilityMessage: BetSliderSetVisibilityMessage = {
            scope: GameUIMessageScope.MAIN_BUTTONS,
            type: GameUIMessageType.BET_SLIDER_SET_VISIBILITY,
            value: visible,
        }

        window.postMessage(setVisibilityMessage)
    })

    // TODO: remove when promo removed from platform
    window.parent.postMessage({ type: GameUIMessageType.CHANGE_OPACITY_PROMO_WIDGET, payload }, '*')
}

const UltraPlayModal: React.FC = () => {
    const isOpen = useUltraPlayStore((s) => s.isOpen)
    const showFeatureChoiceDialog = useUltraPlayStore((s) => s.showFeatureChoiceDialog)
    const showInsufficientBalance = useUltraPlayStore((s) => s.showInsufficientBalance)

    const complianceHeight = useComplianceBarHeight(isOpen)
    const prevIsOpen = React.useRef<boolean | undefined>(undefined)

    React.useEffect(() => {
        // We explicitly check for `true` because `undefined` means Ultra Play was never opened.
        const wasEverOpened = prevIsOpen.current === true

        prevIsOpen.current = isOpen

        const { isAutomaticComponentVisibilityManagementEnabled, isAutomaticComponentVisibilityManagementForced } =
            useComponentStatesStore.getState()

        if (!isAutomaticComponentVisibilityManagementEnabled) {
            return
        }

        if (isOpen) {
            setComponentsVisibility(false)
        } else if (
            wasEverOpened &&
            (!useFreespinsStore.getState().isShowingFreespinsSplash || isAutomaticComponentVisibilityManagementForced)
        ) {
            setComponentsVisibility(true)
        }
    }, [isOpen])

    // Collect any pending win when Ultra Play opens, so a session starts from a clean state.
    React.useEffect(() => {
        if (!isOpen) return

        window.postMessage({ type: GameUIMessageType.COLLECT_CLICKED } as CollectClickedMessage, '*')
    }, [isOpen])

    React.useEffect(() => {
        if (!isOpen) return

        ultraPlayAnalytics.opened()
    }, [isOpen])

    // Disable the Extra Win splash while Ultra Play is open: changing the bet/spins during the splash
    // otherwise crashes the game. Re-enable it on close.
    React.useEffect(() => {
        if (!isOpen) return

        const toggleExtraWin = (status: 'enabled' | 'disabled') =>
            window.postMessage(
                { type: ExtraWinMessages.EXTRA_WIN_TOGGLED, payload: { status } } as ExtraWinToggledMessage,
                '*'
            )

        toggleExtraWin('disabled')

        return () => toggleExtraWin('enabled')
    }, [isOpen])

    React.useEffect(() => {
        if (!isOpen) return

        const blockSpaceKey = (event: KeyboardEvent) => {
            if (event.code !== 'Space') return

            event.preventDefault()
            event.stopPropagation()
        }

        window.addEventListener('keydown', blockSpaceKey, true)
        window.addEventListener('keyup', blockSpaceKey, true)

        return () => {
            window.removeEventListener('keydown', blockSpaceKey, true)
            window.removeEventListener('keyup', blockSpaceKey, true)
        }
    }, [isOpen])

    const handleClose = (reason?: UltraPlayCloseReason) => closeUltraPlay(reason)

    if (!isOpen) return null

    return (
        <div
            className="fixed left-0 right-0 top-0 z-[9998] overflow-hidden text-white bg-ultra-surface"
            style={{ bottom: complianceHeight }}
        >
            <UltraPlaySoundEffects />
            {showFeatureChoiceDialog && <UltraPlayFeatureDialog onClose={handleClose} />}
            {showInsufficientBalance && <UltraPlayInsufficientBalanceModal />}
            <UltraPlayControls onClose={handleClose} />
        </div>
    )
}

export default UltraPlayModal
